import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TopBar } from '../../components/TopBar/TopBar';
import { ViewMoreButton } from '../../components/ViewMoreButton/ViewMoreButton';
import { DonationTeam } from '../../shared/models/DonationTeam';
import { DonationTeamService } from '../../services/DonationTeamService';
import * as WechatAuthService from '../../services/WechatAuthService';
import { formatCalendarDate } from '../../utils/dateFormat';
import './MyDonationTeamsView.css';

const placeholder = '/placeholder.svg';
const PAGE_SIZE = 10;

interface Props {
  onBack: () => void;
  onTeamClick?: (team: DonationTeam) => void;
  initialTab?: 'initiated' | 'joined';
  projectId?: string;
}

function formatRaisedYuan(amount: number): string {
  return amount.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const MyDonationTeamsView: React.FC<Props> = ({ onBack, onTeamClick, initialTab = 'initiated', projectId }) => {
  const [tab, setTab] = useState<'initiated' | 'joined'>(initialTab);
  const [teams, setTeams] = useState<DonationTeam[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const listReqVer = useRef(0);

  const hasMore = total > 0 && teams.length < total;

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    let cancelled = false;
    listReqVer.current += 1;
    const ver = listReqVer.current;
    const load = async () => {
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        if (ver === listReqVer.current) {
          setTeams([]);
          setTotal(0);
          setPage(1);
        }
        return;
      }
      setLoading(true);
      try {
        const resp = await DonationTeamService.listMyTeams({
          openId,
          tab,
          projectId,
          page: 1,
          limit: PAGE_SIZE,
        });
        if (cancelled || ver !== listReqVer.current) return;
        setTeams(resp.list || []);
        setTotal(resp.total ?? 0);
        setPage(1);
      } finally {
        if (!cancelled && ver === listReqVer.current) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tab, projectId]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || loading) return;
    const openId = WechatAuthService.getOpenId();
    if (!openId || total <= teams.length) return;
    const nextPage = page + 1;
    const verAtStart = listReqVer.current;
    setLoadingMore(true);
    try {
      const resp = await DonationTeamService.listMyTeams({
        openId,
        tab,
        projectId,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      if (verAtStart !== listReqVer.current) return;
      const chunk = resp.list || [];
      setTeams((prev) => [...prev, ...chunk]);
      setPage(nextPage);
      if (typeof resp.total === 'number') {
        setTotal(resp.total);
      }
    } finally {
      if (verAtStart === listReqVer.current) setLoadingMore(false);
    }
  }, [loadingMore, loading, total, teams.length, page, tab, projectId]);

  return (
    <div className="my-donation-teams-view">
      <TopBar
        title="捐赠记录"
        tone="white"
        leftIcon="close"
        showBack
        onBack={onBack}
        showMore
        onMore={() => {}}
      />
      <div className="my-dt-body">
        <div className="my-dt-tabs">
          <button
            type="button"
            className={`my-dt-tab${tab === 'initiated' ? ' my-dt-tab--active' : ''}`}
            onClick={() => setTab('initiated')}
          >
            我发起的
          </button>
          <button
            type="button"
            className={`my-dt-tab${tab === 'joined' ? ' my-dt-tab--active' : ''}`}
            onClick={() => setTab('joined')}
          >
            我参与的
          </button>
        </div>

        {loading && <div className="my-dt-hint">加载中...</div>}
        {!loading && teams.length === 0 && <div className="my-dt-hint">暂无团队</div>}

        {!loading && teams.length > 0 && (
          <>
            <div className="my-dt-list">
              {teams.map((team) => {
                const dateStr = formatCalendarDate(team.createTime) || '—';
                const members = team.memberCount ?? 0;
                const raised = team.donationAmount ?? 0;
                return (
                  <button
                    key={team.id}
                    type="button"
                    className="my-dt-card"
                    onClick={() => onTeamClick?.(team)}
                  >
                    <div className="my-dt-card-top">
                      {team.avatar ? (
                        <img
                          src={team.avatar}
                          alt=""
                          className="my-dt-card-avatar"
                          onError={(e) => {
                            e.currentTarget.src = placeholder;
                          }}
                        />
                      ) : (
                        <div className="my-dt-card-avatar my-dt-card-avatar--placeholder" aria-hidden>
                          👥
                        </div>
                      )}
                      <div className="my-dt-card-main">
                        <div className="my-dt-card-row1">
                          <span className="my-dt-card-name">{team.name}</span>
                          <span className="my-dt-card-date">{dateStr}</span>
                        </div>
                        <div className="my-dt-card-slogan">{team.slogan || '一起汇聚爱心'}</div>
                      </div>
                    </div>
                    <div className="my-dt-card-stats">
                      <div className="my-dt-stat-col">
                        <div className="my-dt-stat-label">已召集（人）</div>
                        <div className="my-dt-stat-value">{members}</div>
                      </div>
                      <div className="my-dt-stat-col">
                        <div className="my-dt-stat-label">已筹款（元）</div>
                        <div className="my-dt-stat-value">{formatRaisedYuan(raised)}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {hasMore &&
              (loadingMore ? (
                <div className="my-dt-more-status">加载中...</div>
              ) : (
                <ViewMoreButton className="my-dt-view-more" text="加载更多" onClick={handleLoadMore} />
              ))}
            {!hasMore && <div className="my-dt-more-status my-dt-no-more">没有更多了</div>}
          </>
        )}
      </div>
    </div>
  );
};
